/**
 * Integrytas Group — Email Distribution System (EDS)
 * Page: EDS.RECIPIENT.LIST  —  Recipients & Lists
 *
 * Two-panel page with an internal tab bar:
 *   Lists       — CRUD for recipient lists; click a list to see its members
 *   Recipients  — CRUD for individual email addresses; bulk CSV import
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDate } from '../../lib/format_date';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { compareStrings, useSort } from '../../lib/use_sort';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { UdcRegion } from '../../components/common/UdcRegion';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { hasAccess } from '../../types/auth';
import { registerChildEditor, type ChildEditorProps } from '../../lib/child_editors';
import type {
  EdsRecipientListRow,
  EdsRecipientRow,
  EdsListRecipientRow,
  EdsListSortKey,
  EdsRecipientSortKey,
} from '../../types/eds';

const PAGE_CODE = 'EDS.RECIPIENT.LIST';
registerUdcHost(PAGE_CODE, 'eds_recipient_list', 'Recipient Lists');
registerUdcHost(PAGE_CODE, 'eds_recipient', 'Recipients');

type PageTab = 'lists' | 'recipients';

const RECIPIENT_GRID_COLS: GridColumnDef[] = [
  { key: 'email_address',          header: 'Email',      defaultWidth: 220 },
  { key: 'first_name',             header: 'First Name', defaultWidth: 120 },
  { key: 'last_name',              header: 'Last Name',  defaultWidth: 120 },
  { key: 'is_globally_opted_out',  header: 'Opted Out',  defaultWidth: 100 },
  { key: 'is_active',              header: 'Active',     defaultWidth: 80  },
];

export interface EdsRecipientPageProps { session: AuthSession; }

export function EdsRecipientPage({ session }: EdsRecipientPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <EdsRecipientPageInner session={session} />
    </PersonalizationProvider>
  );
}

function EdsRecipientPageInner({ session }: EdsRecipientPageProps) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const [tab, setTab] = useState<PageTab>('lists');

  return (
    <div className="flex h-full flex-col">
      {/* Tab bar */}
      <div className="flex h-9 shrink-0 items-center gap-0 border-b border-csm-border bg-white px-3">
        {(['lists', 'recipients'] as PageTab[]).map(tabKey => (
          <button key={tabKey} onClick={() => setTab(tabKey)}
                  className={`mr-1 h-7 rounded-t px-4 text-csm-sm capitalize transition-colors
                    ${tab === tabKey ? 'border border-b-0 border-csm-border bg-white font-semibold text-primary' : 'text-csm-ink-soft hover:text-csm-ink'}`}>
            {tabKey === 'lists' ? t('eds.recipients.tab_lists') : t('eds.recipients.tab_recipients')}
          </button>
        ))}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {isPersonalizing && (
        <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
          {t('eds.recipients.personalization_hint', 'Personalization mode — configure columns, then click the icon to save and exit.')}
        </div>
      )}
      <div className="min-h-0 flex-1">
        {tab === 'lists'      && <ListsPanel session={session} />}
        {tab === 'recipients' && <RecipientsPanel session={session} />}
      </div>
    </div>
  );
}

// ===========================================================================
// LISTS PANEL
// ===========================================================================

function ListsPanel({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [lists, setLists]           = useState<EdsRecipientListRow[]>([]);
  const [filter, setFilter]         = useState('');
  const [editing, setEditing]       = useState<EdsRecipientListRow | null>(null);
  const [creating, setCreating]     = useState(false);
  const [selectedList, setSelectedList] = useState<EdsRecipientListRow | null>(null);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const sort = useSort<EdsListSortKey>('eds.lists.sort', { key: 'list_name', dir: 'asc' });
  // Custom fields placed on recipient lists: shown inline on each card + as facets.
  const udcLists = useUdcGrid('eds_recipient_list', PAGE_CODE, { pkColumn: 'list_id', rows: lists as unknown as Record<string, unknown>[] });

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('eds_recipient_list')
        .select(`*, eds_recipient_list_tl!left(list_name, language_id)`)
        .order('internal_code');
      if (e) throw e;
      type ListTl = TlRowText & { list_name?: string };
      type Row = EdsRecipientListRow & { eds_recipient_list_tl?: ListTl[] };
      const flat = ((data ?? []) as Row[]).map(r => {
        const ltls = r.eds_recipient_list_tl ?? [];
        const tl = ltls.find(t => t.language_id === activeLocale)   // §8: active locale first
                   ?? ltls.find(t => t.language_id === 'EN_US') ?? ltls[0];
        return { ...r, list_name: tl?.list_name ?? r.internal_code } as EdsRecipientListRow;
      });
      setLists(flat);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [activeLocale]);

  useEffect(() => { void reload(); }, [reload]);

  const filtered = useMemo(() => {
    const base = lists.filter(r =>
      (!filter.trim() ||
      r.internal_code.toLowerCase().includes(filter.toLowerCase()) ||
      (r.list_name ?? '').toLowerCase().includes(filter.toLowerCase()))
      && udcLists.matchRow(r as unknown as Record<string, unknown>)
    );
    return sort.sorted(base, (a, b, key) => compareStrings(String((a as unknown as Record<string, unknown>)[key] ?? ''), String((b as unknown as Record<string, unknown>)[key] ?? '')));
  }, [lists, filter, sort, udcLists]);

  return (
    <div className="flex h-full">
      {/* Lists grid */}
      <div className="flex w-80 shrink-0 flex-col border-r border-csm-border">
        <div className="flex h-9 shrink-0 items-center gap-1 border-b border-csm-border bg-white px-2">
          <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
                 placeholder={t('eds.recipients.lists.filter_placeholder', 'Filter lists…')} className="h-7 flex-1 rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none" />
          <UdcFacets grid={udcLists} />
          {canAdd && (<button onClick={() => setCreating(true)} className="h-7 rounded border border-primary bg-primary px-2 text-csm-xs text-white hover:bg-primary-hover">+</button>)}
        </div>
        {error && (
          <div className="px-2 pt-1">
            <ErrorBanner
              message={t('eds.recipients.lists.load_failed', "Couldn't load lists.")}
              detail={error}
              retry_label={t('common.retry', 'Retry')}
              on_retry={() => void reload()}
              details_label={t('common.details', 'Details')}
            />
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {filtered.map(r => (
            <div key={r.list_id}
                 onClick={() => setSelectedList(r)}
                 className={`flex cursor-pointer items-center justify-between border-b border-csm-border-soft px-3 py-2
                   ${selectedList?.list_id === r.list_id ? 'bg-primary-soft' : 'hover:bg-csm-surface-alt'}`}>
              <div>
                <div className="text-csm-sm font-medium text-csm-ink">{r.list_name}</div>
                <div className="font-mono text-csm-2xs text-csm-muted">{r.internal_code}</div>
                {udcLists.columns.map(c => {
                  const v = udcLists.renderCell(r as unknown as Record<string, unknown>, c.key);
                  return v ? <div key={c.key} className="text-csm-2xs text-csm-muted"><span className="font-medium">{c.header}:</span> {v}</div> : null;
                })}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <YesNoBadge value={r.is_active} yesLabel={t('eds.recipients.lists.active_on', 'On')} noLabel={t('eds.recipients.lists.active_off', 'Off')} />
                {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }}
                        className="rounded border border-csm-border bg-white px-1.5 py-0.5 text-csm-2xs hover:bg-csm-surface-alt">✎</button>)}
              </div>
            </div>
          ))}
          {filtered.length === 0 && !busy && (
            <div className="px-3 py-6">
              <EmptyState
                variant={filter.trim() ? 'no-match' : 'no-data'}
                title={t('eds.recipients.lists.no_results')}
                action={!filter.trim()
                  ? canAdd && (<button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">+ {t('common.new', 'New')}</button>)
                  : undefined}
              />
            </div>
          )}
        </div>
      </div>

      {/* Members panel */}
      <div className="flex-1 overflow-hidden">
        {selectedList
          ? <ListMembersPanel session={session} list={selectedList} />
          : <div className="flex h-full items-center justify-center text-csm-sm italic text-csm-muted">{t('eds.recipients.lists.select_prompt', 'Select a list to view its members.')}</div>
        }
      </div>

      {(editing || creating) && (
        <ListEditModal
          session={session}
          list={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List members sub-panel
// ---------------------------------------------------------------------------

function ListMembersPanel({ session: _session, list }: { session: AuthSession; list: EdsRecipientListRow }) {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const [members, setMembers]       = useState<EdsListRecipientRow[]>([]);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('eds_list_recipient')
        .select(`*, eds_recipient!left(email_address, first_name, last_name)`)
        .eq('list_id', list.list_id)
        .order('subscribed_at', { ascending: false });
      if (e) throw e;
      type Row = EdsListRecipientRow & {
        eds_recipient?: { email_address?: string; first_name?: string | null; last_name?: string | null } | null;
      };
      const flat = ((data ?? []) as Row[]).map(r => ({
        ...r,
        email_address: r.eds_recipient?.email_address ?? '',
        first_name: r.eds_recipient?.first_name ?? '',
        last_name: r.eds_recipient?.last_name ?? '',
      })) as EdsListRecipientRow[];
      setMembers(flat);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [list.list_id]);

  useEffect(() => { void load(); }, [load]);

  const removeMember = async (r: EdsListRecipientRow) => {
    if (!confirm(t('eds.recipients.lists.members.remove_confirm', 'Remove {{email}} from this list?', { email: r.email_address }))) return;
    setBusy(true);
    try {
      // FLAGGED: eds_list_recipient is a composite-key junction (list_id + recipient_id) with
      // no single surrogate PK; the routed helpers take one PK column. Left as a direct update
      // pending a composite-key route through the Common Routine.
      await apiDb.from('eds_list_recipient')
        .update({ is_active: 'N' })
        .eq('list_id', r.list_id).eq('recipient_id', r.recipient_id);
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-csm-border bg-csm-surface-alt px-3">
        <span className="text-csm-sm font-semibold text-csm-ink">{list.list_name}</span>
        <span className="text-csm-xs text-csm-muted">{members.length} member{members.length !== 1 ? 's' : ''}</span>
        <button onClick={() => void load()} className="ml-auto h-6 rounded border border-csm-border bg-white px-2 text-csm-xs hover:bg-csm-border-soft">{t('common.refresh')}</button>
      </div>
      {error && (
        <div className="px-3 py-1">
          <ErrorBanner
            message={t('eds.recipients.lists.members.load_failed', "Couldn't load list members.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void load()}
            details_label={t('common.details', 'Details')}
          />
        </div>
      )}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full border-collapse text-csm-sm">
          <thead className="sticky top-0 bg-white text-csm-xs uppercase tracking-wider text-csm-ink-soft">
            <tr>
              <th className="border-b border-csm-border px-3 py-1.5 text-left">{t('eds.recipients.lists.members.columns.email')}</th>
              <th className="border-b border-csm-border px-3 py-1.5 text-left">{t('eds.recipients.lists.members.columns.name')}</th>
              <th className="border-b border-csm-border px-3 py-1.5 text-left">{t('eds.recipients.lists.members.columns.source_code')}</th>
              <th className="border-b border-csm-border px-3 py-1.5 text-left">{t('eds.recipients.lists.members.columns.subscribed_at')}</th>
              <th className="border-b border-csm-border px-3 py-1.5 text-left">{t('eds.recipients.lists.members.columns.is_active')}</th>
              <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {members.map(r => (
              <tr key={r.recipient_id} className="hover:bg-csm-surface-alt">
                <td className="border-b border-csm-border-soft px-3 py-1.5 font-mono text-csm-xs">{r.email_address}</td>
                <td className="border-b border-csm-border-soft px-3 py-1.5">{[r.first_name, r.last_name].filter(Boolean).join(' ') || <span className="italic text-csm-muted">—</span>}</td>
                <td className="border-b border-csm-border-soft px-3 py-1.5 text-csm-muted">{r.source_code}{/* fk-display-ok: recipient-source enum (MANUAL/IMPORT), no lookup entity */}</td>
                <td className="border-b border-csm-border-soft px-3 py-1.5 text-csm-muted">{formatDate(r.subscribed_at)}</td>
                <td className="border-b border-csm-border-soft px-3 py-1.5"><YesNoBadge value={r.is_active} yesLabel={t('common.boolean.yes', 'Yes')} noLabel={t('common.boolean.no', 'No')} /></td>
                <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                  <button onClick={() => void removeMember(r)}
                          className="rounded border border-csm-danger-border bg-csm-danger-soft px-1.5 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">
                    {t('eds.recipients.lists.members.remove')}
                  </button>
                </td>
              </tr>
            ))}
            {members.length === 0 && !busy && (
              <tr><td colSpan={6} className="px-3 py-6 text-center">
                <EmptyState variant="no-data" title={t('eds.recipients.lists.members.empty')} />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// List edit modal
// ---------------------------------------------------------------------------

function ListEditModal({ session, list, onClose, onSaved }: { session: AuthSession; list: EdsRecipientListRow | null; onClose: () => void; onSaved: () => void; }) {
  const { t } = useTranslation();
  const isCreate = list === null;
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [codeError, setCodeError]     = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);

  const buildInitial = () => ({
    internalCode: list?.internal_code ?? '',
    listName:     list?.list_name ?? '',
    isActive:     list ? list.is_active === 'Y' : true,
  });
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(buildInitial());

  // Reset form when the modal opens with a new list value
  useEffect(() => { reset(buildInitial()); }, [list]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); } else { onClose(); }
  };

  const validateCode = () => {
    if (!isCreate) return;
    const code = form.internalCode.trim();
    if (code && !/^[A-Z][A-Z0-9_.]*$/.test(code)) {
      setCodeError(t('eds.recipients.lists.drawer.code_error', 'Must start with a letter — uppercase letters, numbers, dots only.'));
    } else {
      setCodeError(null);
    }
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!form.listName.trim()) throw new Error(t('eds.recipients.lists.error_name_required', 'List name is required.'));
      let listId = list?.list_id ?? '';
      if (isCreate) {
        if (!form.internalCode.trim()) throw new Error(t('eds.recipients.lists.error_code_required', 'Internal code is required.'));
        if (!/^[A-Z][A-Z0-9_.]*$/.test(form.internalCode.trim())) throw new Error(t('eds.recipients.lists.error_code_format', 'Internal code must start with a letter — uppercase letters, numbers, dots only.'));
        const { row_id, error: e } = await routedInsert(session, 'eds_recipient_list', {
          internal_code: form.internalCode.trim().toUpperCase(), is_active: form.isActive ? 'Y' : 'N',
        });
        if (e) throw e;
        listId = String(row_id);
      } else {
        const { error: e } = await routedUpdate(session, 'eds_recipient_list', 'list_id', listId, { is_active: form.isActive ? 'Y' : 'N' });
        if (e) throw e;
      }
      // FLAGGED: eds_recipient_list_tl has a composite PK (list_id, language_id); the routed
      // helpers take a single PK column. Left as a direct upsert pending a composite-key route.
      await apiDb.from('eds_recipient_list_tl').upsert({ list_id: listId, language_id: 'EN_US', list_name: form.listName.trim() }, { onConflict: 'list_id,language_id' });
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Modal
        title={isCreate ? t('eds.recipients.lists.drawer.new_title') : t('eds.recipients.lists.drawer.edit_title', { code: list?.internal_code ?? '' })}
        onClose={handleClose}
        width="lg"
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel')}</button>
            <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving') : t('common.save')}</button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.recipients.lists.drawer.fields.internal_code')}</label>
            <input type="text" value={form.internalCode} disabled={!isCreate}
                   onChange={e => setForm({ ...form, internalCode: e.target.value.toUpperCase() })}
                   onBlur={validateCode}
                   placeholder="EDS.LIST.NEWSLETTER"
                   className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted" />
            {isCreate && !codeError && (
              <p className="text-csm-xs text-csm-ink-soft mt-1">{t('eds.recipients.lists.drawer.code_hint', 'E.g. EDS.LIST.NEWSLETTER — uppercase, dots only')}</p>
            )}
            {isCreate && codeError && (
              <p className="text-csm-xs text-red-600 mt-1">{codeError}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.recipients.lists.drawer.fields.list_name')} <span className="text-csm-danger">*</span></label>
            <input type="text" value={form.listName} onChange={e => setForm({ ...form, listName: e.target.value })}
                   placeholder={t('eds.recipients.lists.drawer.name_placeholder', 'Monthly Newsletter')} className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none" />
          </div>
          <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
            <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="h-4 w-4 accent-primary" />
            {t('eds.recipients.lists.drawer.fields.is_active')}
          </label>
          <UdcRegion entity="eds_recipient_list" pageCode={PAGE_CODE} pkColumn="list_id" pk={list?.list_id} />
          {error && <div className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
        </div>
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('eds.discard.title', 'Discard changes?')}
          message={t('eds.discard.message', 'You have unsaved changes. Are you sure you want to discard them?')}
          confirmLabel={t('eds.discard.confirm', 'Discard')}
          cancelLabel={t('eds.discard.cancel', 'Keep editing')}
          onConfirm={onClose}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

// ===========================================================================
// RECIPIENTS PANEL
// ===========================================================================

function RecipientsPanel({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [rows, setRows]             = useState<EdsRecipientRow[]>([]);
  const [filter, setFilter]         = useState('');
  // §17.B5: per-column multi-value facets (opted-out, active).
  const [facets, setFacets]         = useState<{ opted: string[]; active: string[] }>({ opted: [], active: ['Y'] });
  const [editing, setEditing]       = useState<EdsRecipientRow | null>(null);
  const [creating, setCreating]     = useState(false);
  const [busy, setBusy]             = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [importMsg, setImportMsg]   = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const sort = useSort<EdsRecipientSortKey>('eds.recipients.sort', { key: 'email_address', dir: 'asc' });
  const detail = useExpandedRows<string>();
  // §17.B13 relation: the recipient lists each recipient belongs to.
  const [listsByRecipient, setListsByRecipient] = useState<Map<string, { list_id: string; name: string }[]>>(new Map());

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('eds_recipient').select('*').order('email_address');
      if (e) throw e;
      setRows((data ?? []) as EdsRecipientRow[]);

      // The lists each recipient is a member of (eds_list_recipient junction),
      // list names resolved via _tl. RLS-respecting (the caller's JWT applies).
      const [{ data: listRows }, { data: memRows }] = await Promise.all([
        apiDb.from('eds_recipient_list').select('list_id, internal_code, eds_recipient_list_tl!left(list_name)'),
        apiDb.from('eds_list_recipient').select('recipient_id, list_id'),
      ]);
      const listName = new Map<string, string>();
      for (const l of (listRows ?? []) as { list_id: string; internal_code: string; eds_recipient_list_tl?: { list_name: string }[] }[]) {
        listName.set(l.list_id, l.eds_recipient_list_tl?.[0]?.list_name ?? l.internal_code);
      }
      const byRecipient = new Map<string, { list_id: string; name: string }[]>();
      for (const m of (memRows ?? []) as { recipient_id: string; list_id: string }[]) {
        const arr = byRecipient.get(m.recipient_id) ?? [];
        arr.push({ list_id: m.list_id, name: listName.get(m.list_id) ?? m.list_id });
        byRecipient.set(m.recipient_id, arr);
      }
      setListsByRecipient(byRecipient);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  // User-defined columns / facets an admin placed on this page (runtime, no code).
  const udcGrid = useUdcGrid('eds_recipient', PAGE_CODE, { pkColumn: 'recipient_id', rows: rows as unknown as Record<string, unknown>[] });

  // §17.B5: static yes/no facet options (logical columns of the list).
  const facetOptions = useMemo(() => ({
    opted:  [{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }],
    active: [{ value: 'Y', label: t('common.boolean.active') }, { value: 'N', label: t('common.boolean.inactive') }],
  }), [t]);

  const facetsActive = facets.opted.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => {
    setFilter('');
    setFacets({ opted: [], active: [] });
  };

  const filtered = useMemo(() => {
    const base = rows.filter(r =>
      (!filter.trim() ||
      r.email_address.toLowerCase().includes(filter.toLowerCase()) ||
      (r.first_name ?? '').toLowerCase().includes(filter.toLowerCase()) ||
      (r.last_name ?? '').toLowerCase().includes(filter.toLowerCase()))
      // OR within a facet, AND across facets.
      && (facets.opted.length  === 0 || facets.opted.includes(r.is_globally_opted_out))
      && (facets.active.length === 0 || facets.active.includes(r.is_active))
      && udcGrid.matchRow(r as unknown as Record<string, unknown>)
    );
    return sort.sorted(base, (a, b, key) => compareStrings(String((a as unknown as Record<string, unknown>)[key] ?? ''), String((b as unknown as Record<string, unknown>)[key] ?? '')));
  }, [rows, filter, facets, sort, udcGrid]);

  // §17.B13 related-children: recipient's list memberships aren't loaded upfront here.
  // TODO: wire relations if per-recipient list membership is ever pre-loaded.
  const buildRelatedGroups = (id: string): RelatedChildGroup[] => [
    {
      key: 'lists',
      table: 'eds_recipient_list', pkColumn: 'list_id',
      title: t('eds.recipients.related.lists', 'Member of lists'),
      items: (listsByRecipient.get(id) ?? []).map(l => ({ id: l.list_id, label: l.name })),
    },
  ];
  const hasRelated = (id: string) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true); setImportMsg(null); setError(null);
    try {
      const text = await file.text();
      const { data: parsed } = Papa.parse<Record<string, string>>(text, { header: true, skipEmptyLines: true });
      let inserted = 0, skipped = 0;
      for (const row of parsed) {
        const email = (row['email_address'] ?? row['email'] ?? row['Email'] ?? '').trim().toLowerCase();
        if (!email) { skipped++; continue; }
        const fields = {
          email_address: email,
          first_name: row['first_name'] ?? row['First Name'] ?? null,
          last_name:  row['last_name']  ?? row['Last Name']  ?? null,
          is_active: 'Y',
        };
        // §2: upsert-by-email resolves to a single row by the surrogate PK first,
        // then routes a CHANGE (existing) or ADD (new) through the Common Routine.
        const { data: existing } = await apiDb.from('eds_recipient')
          .select('recipient_id').eq('email_address', email).maybeSingle();
        const { error: e } = existing
          ? await routedUpdate(session, 'eds_recipient', 'recipient_id', (existing as { recipient_id: string }).recipient_id, fields)
          : await routedInsert(session, 'eds_recipient', fields);
        if (e) { skipped++; } else { inserted++; }
      }
      setImportMsg(t('eds.recipients.grid.import_complete', 'Import complete — {{inserted}} upserted, {{skipped}} skipped.', { inserted, skipped }));
      await reload();
    } catch (e) { setError((e as Error).message); }
    finally { setImportBusy(false); e.target.value = ''; }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
               placeholder={t('eds.recipients.grid.search_placeholder', 'Search recipients…')}
               className="h-7 w-72 rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none" />
        <FacetFilter label={t('eds.recipients.grid.columns.opted_out', 'Opted Out')} options={facetOptions.opted}  selected={facets.opted}  onChange={v => setFacets(f => ({ ...f, opted: v }))} />
        <FacetFilter label={t('eds.recipients.grid.columns.active', 'Active')}        options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
        <UdcFacets grid={udcGrid} />
        <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('common.refresh')}</button>
        {canAdd && (<button onClick={() => setCreating(true)} className="h-7 rounded border border-primary bg-primary px-2 text-csm-sm text-white hover:bg-primary-hover">{t('eds.recipients.grid.new_recipient')}</button>)}
        <label className={`flex h-7 cursor-pointer items-center rounded border px-2 text-csm-sm transition-colors
          ${importBusy ? 'border-csm-border bg-csm-muted text-white cursor-wait' : 'border-csm-border bg-white text-csm-ink-soft hover:bg-csm-surface-alt'}`}>
          {importBusy ? t('eds.recipients.grid.import_modal.importing') : t('eds.recipients.grid.import')}
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="sr-only" onChange={handleCsvImport} disabled={importBusy} />
        </label>
        <span className="ml-auto text-csm-xs text-csm-muted">{filtered.length} recipient{filtered.length !== 1 ? 's' : ''}</span>
        {busy  && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
        {importMsg && <span className="text-csm-xs text-csm-success">{importMsg}</span>}
      </div>
      {error && (
        <div className="shrink-0 px-3 pt-2">
          <ErrorBanner
            message={t('eds.recipients.grid.load_failed', "Couldn't load recipients.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        </div>
      )}
      <div className="flex-1 overflow-auto">
        {busy && rows.length === 0 ? (
          <SkeletonRows rows={8} />
        ) : (
        <PersonalizedGrid componentKey="recipients_grid" columns={[...RECIPIENT_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    udcGrid.isUdcKey(col.key)
                      ? <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                            style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined}>{col.header}</th>
                      : <SortableHeader<EdsRecipientSortKey> key={col.key}
                          sortKey={col.key as EdsRecipientSortKey} label={col.header}
                          currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                          style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.recipient_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.recipient_id)}
                        hasChildren={hasRelated(r.recipient_id)}
                        onToggle={() => detail.toggle(r.recipient_id)}
                        expandLabel={t('common.related.show', 'Show related records')}
                        collapseLabel={t('common.related.hide', 'Hide related')}
                        tooltip={t('common.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'email_address'         && <span className="font-mono text-csm-sm">{r.email_address}</span>}
                        {col.key === 'first_name'            && (r.first_name ?? <span className="italic text-csm-muted">—</span>)}
                        {col.key === 'last_name'             && (r.last_name ?? <span className="italic text-csm-muted">—</span>)}
                        {col.key === 'is_globally_opted_out' && (
                          r.is_globally_opted_out === 'Y'
                            ? <span className="rounded bg-csm-danger-soft px-1.5 py-0.5 text-csm-xs font-medium text-csm-danger border border-csm-danger-border">{t('eds.recipients.grid.columns.opted_out', 'Opted Out')}</span>
                            : <span className="text-csm-muted text-csm-xs">—</span>
                        )}
                        {col.key === 'is_active' && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.inactive', 'Inactive')} />}
                        {udcGrid.isUdcKey(col.key) && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }}
                              className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('common.edit')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.recipient_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.recipient_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr>
                    <td colSpan={visibleCols.length + 2} className="py-12 text-center">
                      <EmptyState
                        variant={anyFilterActive ? 'no-match' : 'no-data'}
                        title={t('eds.recipients.grid.no_results')}
                        action={!anyFilterActive
                          ? canAdd && (<button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">+ {t('eds.recipients.grid.new_recipient')}</button>)
                          : undefined}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
      </div>
      {(editing || creating) && (
        <RecipientModal
          session={session}
          recipient={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recipient edit modal
// ---------------------------------------------------------------------------

const RECIPIENT_NAME_FIELD_DEFS: FieldDef[] = [
  { key: 'first_name', type: 'text', label: 'First Name', col_span: 1 },
  { key: 'last_name',  type: 'text', label: 'Last Name',  col_span: 1 },
];
const RECIPIENT_NAME_CORE_LAYOUT = toUldCoreLayout(RECIPIENT_NAME_FIELD_DEFS);

function RecipientModal({ session, recipient, onClose, onSaved }: { session: AuthSession; recipient: EdsRecipientRow | null; onClose: () => void; onSaved: () => void; }) {
  const { t } = useTranslation();
  const isCreate = recipient === null;
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);

  const buildInitial = () => ({
    email:     recipient?.email_address ?? '',
    firstName: recipient?.first_name ?? '',
    lastName:  recipient?.last_name ?? '',
    optedOut:  recipient?.is_globally_opted_out === 'Y',
    isActive:  recipient ? recipient.is_active === 'Y' : true,
  });
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(buildInitial());
  // Custom fields placed on this entity (host = the list page, PAGE_CODE) become
  // first-class FieldRenderer items: they interleave with native fields and inherit
  // the same per-tier personalization (hide / read-only / required / reorder).
  const udcForm = useUdcFormFields('eds_recipient', PAGE_CODE, { pkColumn: 'recipient_id', pk: recipient?.recipient_id });
  const nameFieldDefs = useMemo(() => [...RECIPIENT_NAME_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const nameCoreLayout = useMemo(() => [...RECIPIENT_NAME_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const { layout: nameLayout } = useUldLayout('EDS.RECIPIENT.EDIT.MODAL', nameCoreLayout);

  // Reset form when modal opens with a new recipient value
  useEffect(() => { reset(buildInitial()); }, [recipient]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); } else { onClose(); }
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!form.email.trim()) throw new Error(t('eds.recipients.grid.error_email_required', 'Email address is required.'));
      const payload = {
        email_address: form.email.trim().toLowerCase(),
        first_name: form.firstName.trim() || null,
        last_name: form.lastName.trim() || null,
        is_globally_opted_out: form.optedOut ? 'Y' : 'N',
        is_active: form.isActive ? 'Y' : 'N',
      };
      if (isCreate) {
        const { error: e } = await routedInsert(session, 'eds_recipient', payload);
        if (e) throw e;
      } else {
        const { error: e } = await routedUpdate(session, 'eds_recipient', 'recipient_id', recipient?.recipient_id, payload);
        if (e) throw e;
      }
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Modal
        title={isCreate ? t('eds.recipients.grid.drawer.new_title') : t('eds.recipients.grid.drawer.edit_title', { code: recipient?.email_address ?? '' })}
        onClose={handleClose}
        width="lg"
        headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel')}</button>
            <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving') : t('common.save')}</button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.recipients.grid.drawer.fields.email_address')} <span className="text-csm-danger">*</span></label>
            <input type="email" value={form.email} disabled={!isCreate} onChange={e => setForm({ ...form, email: e.target.value })}
                   className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted" />
          </div>
          <FieldRenderer
            fields={nameFieldDefs}
            layout={nameLayout}
            columns={2}
            pageCode="EDS.RECIPIENT.EDIT.MODAL"
            session={session}
            render={(f) => {
              if (f.key === 'first_name') return (
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.recipients.grid.drawer.fields.first_name')}</label>
                  <input type="text" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })}
                         className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none" />
                </div>
              );
              if (f.key === 'last_name') return (
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.recipients.grid.drawer.fields.last_name')}</label>
                  <input type="text" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })}
                         className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none" />
                </div>
              );
              if (udcForm.isUdcKey(f.key)) return (
                <PersonalizedField componentKey="drawer" fieldKey={f.key} label={f.label}
                  readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                  {udcForm.renderControl(f.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
          <div className="space-y-1 rounded border border-csm-border p-3">
            <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
              <input type="checkbox" checked={form.optedOut} onChange={e => setForm({ ...form, optedOut: e.target.checked })} className="h-4 w-4 accent-csm-danger" />
              {t('eds.recipients.grid.drawer.fields.is_globally_opted_out')}
              <span className="text-csm-2xs text-csm-muted">{t('eds.recipients.grid.drawer.opted_out_hint', '(blocks all future sends)')}</span>
            </label>
            <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="h-4 w-4 accent-primary" />
              {t('eds.recipients.grid.drawer.fields.is_active')}
            </label>
          </div>
          {error && <div className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
        </div>
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('eds.discard.title', 'Discard changes?')}
          message={t('eds.discard.message', 'You have unsaved changes. Are you sure you want to discard them?')}
          confirmLabel={t('eds.discard.confirm', 'Discard')}
          cancelLabel={t('eds.discard.cancel', 'Keep editing')}
          onConfirm={onClose}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// §17.B13 drill-down — this page owns the recipient-list editor and registers
// it for other pages' related-children panels (module-load side effect).
// ---------------------------------------------------------------------------
function RecipientListChildEditor({ session, pk, onClose, onSaved }: ChildEditorProps) {
  const { t } = useTranslation();
  const [row, setRow] = useState<EdsRecipientListRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Same shape the list page reads, scoped to the one row.
      const { data, error: e } = await apiDb
        .from('eds_recipient_list')
        .select('*, eds_recipient_list_tl!left(list_name, language_id)')
        .eq('list_id', pk).maybeSingle();
      if (cancelled) return;
      if (e) setError(e.message); else setRow((data as EdsRecipientListRow) ?? null);
      setBusy(false);
    })();
    return () => { cancelled = true; };
  }, [pk]);

  if (busy || error || !row) {
    return (
      <Modal title={t('eds.lists.edit_title', 'Recipient List')} onClose={onClose} width="lg">
        <div className={'px-1 py-3 text-csm-sm ' + (error ? 'text-csm-danger' : 'text-csm-muted')}>
          {error ?? (busy ? t('common.loading', 'Loading…') : t('common.not_found', 'Record not found.'))}
        </div>
      </Modal>
    );
  }
  return <ListEditModal session={session} list={row} onClose={onClose} onSaved={onSaved} />;
}

registerChildEditor('eds_recipient_list', {
  pageCode: PAGE_CODE,
  title: 'Recipient List',
  Editor: RecipientListChildEditor,
});
